// Tab and breakpoint management for the dashboard layout editor.
import DashboardBreakpointHelper from "../helpers/DashboardBreakpointHelper";

/**
 * Breakpoints start from the original layout, but each breakpoint needs its
 * own positioning objects so later edits stay isolated per breakpoint.
 */
function cloneWidgetPositioning(widgetPositioning = []) {
    return widgetPositioning.map((positioning) => ({
        ...positioning,
    }));
}

const dashboardLayoutEditorTabs = {
    /**
     * Get default dashboard tab id.
     */
    getDefaultDashboardTabId() {
        if (!this.dashboardTabs.length || !this.dashboardTabs[0].id) {
            return 'default';
        }

        return this.dashboardTabs[0].id;
    },
    /**
     * Get active dashboard tab id.
     */
    getActiveDashboardTabId() {
        return this.activeDashboardTabId || this.getDefaultDashboardTabId();
    },
    /**
     * Ensure active dashboard tab.
     */
    ensureActiveDashboardTab() {
        if (!this.dashboardTabs.length) {
            this.dashboardTabs = [{
                id: 'default',
                title: `${this.$t('Tab')} 1`,
            }];
        }

        if (!this.hasDashboardTab(this.activeDashboardTabId)) {
            this.activeDashboardTabId = this.dashboardTabs[0].id;
        }
    },
    /**
     * Check whether dashboard tab.
     *
     * @param {*} tabId
     */
    hasDashboardTab(tabId) {
        for (let index = 0; index < this.dashboardTabs.length; index += 1) {
            if (this.dashboardTabs[index].id === tabId) {
                return true;
            }
        }

        return false;
    },
    /**
     * Get new dashboard tab id.
     */
    getNewDashboardTabId() {
        return `dashboard-tab-${Date.now()}-${Math.round(Math.random() * 1000)}`;
    },
    /**
     * Add dashboard tab.
     */
    addDashboardTab() {
        const nextIndex = this.dashboardTabs.length + 1;
        const tab = {
            id: this.getNewDashboardTabId(),
            title: `${this.$t('Tab')} ${nextIndex}`,
        };

        this.dashboardTabs = [...this.dashboardTabs, tab];
        this.activeDashboardTabId = tab.id;
        this.dataProvider.dashboardTabs = this.dashboardTabs;
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Persist a complete tab order from the sorting dialog while retaining the
     * tab ids that connect widgets to their respective tabs.
     *
     * @param {Array} tabs
     */
    sortDashboardTabs(tabs) {
        if (!Array.isArray(tabs) || tabs.length !== this.dashboardTabs.length) {
            return;
        }

        const currentTabIds = new Set(this.dashboardTabs.map((tab) => tab.id));
        const sortedTabIds = new Set(tabs.map((tab) => tab.id));
        if (sortedTabIds.size !== tabs.length || tabs.some((tab) => !currentTabIds.has(tab.id))) {
            return;
        }

        this.dashboardTabs = [...tabs];
        this.dataProvider.dashboardTabs = this.dashboardTabs;
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Rename dashboard tab.
     *
     * @param {{tab: *, title: string}} renameData
     */
    renameDashboardTab(renameData = {}) {
        const { tab, title } = renameData;

        if (!tab || !this.hasDashboardTab(tab.id)) {
            return;
        }

        const normalizedTitle = String(title || '').trim();

        if (!normalizedTitle) {
            return;
        }

        this.dashboardTabs = this.dashboardTabs.map((currentTab) => currentTab.id === tab.id
            ? {
                ...currentTab,
                title: normalizedTitle,
            }
            : currentTab);
        this.dataProvider.dashboardTabs = this.dashboardTabs;
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Delete dashboard tab.
     *
     * @param {*} tab
     */
    deleteDashboardTab(tab) {
        if (this.dashboardTabs.length <= 1) {
            return;
        }

        const referenceIdsToDelete = this.getDashboardTabWidgetReferenceIds(tab.id);

        referenceIdsToDelete.forEach((referenceId) => {
            this.deleteWidget(this.blocks, referenceId, false, false);
        });

        this.breakpoints.forEach((breakpoint) => {
            breakpoint.widgetPositioning = breakpoint.widgetPositioning.filter((positioning) => !referenceIdsToDelete.includes(positioning.referenceId));
        });

        this.dashboardTabs = this.dashboardTabs.filter((currentTab) => currentTab.id !== tab.id);
        this.activeDashboardTabId = this.dashboardTabs[0]?.id || null;
        this.dataProvider.dashboardTabs = this.dashboardTabs;
        this.triggerDataProviderEvent('propertyChanged');
    },
    /**
     * Get dashboard tab widget reference ids.
     *
     * @param {*} tabId
     * @param {*} blocks
     */
    getDashboardTabWidgetReferenceIds(tabId, blocks = this.blocks) {
        const referenceIds = [];

        blocks.forEach((block) => {
            const currentTabId = block.tabId || this.getDefaultDashboardTabId();

            if (currentTabId === tabId) {
                referenceIds.push(block.referenceId);
            }

            if (Array.isArray(block.blocks) && block.blocks.length) {
                const childReferenceIds = this.getDashboardTabWidgetReferenceIds(tabId, block.blocks);

                childReferenceIds.forEach((childReferenceId) => {
                    referenceIds.push(childReferenceId);
                });
            }
        });

        return referenceIds;
    },
    /**
     * Add breakpoint.
     *
     * @param {*} breakpointToAdd
     * @param {*} index
     */
    addBreakpoint(breakpointToAdd, index) {
        breakpointToAdd.widgetPositioning = cloneWidgetPositioning(
            this.originalBreakpoint?.widgetPositioning || []
        );
        this.breakpoints.splice(index, 0, breakpointToAdd);
    },
    /**
     * Remove breakpoint.
     *
     * @param {*} breakpointToRemove
     */
    removeBreakpoint(breakpointToRemove) {
        const indexToRemove = this.breakpoints.findIndex((breakpoint) => breakpoint.id === breakpointToRemove.id);
        this.breakpoints.splice(indexToRemove, 1);
    },
    /**
     * Get breakpoint width.
     *
     * @param {*} breakpoint
     */
    getBreakpointWidth(breakpoint) {
        return DashboardBreakpointHelper.getBreakpointWidth(breakpoint);
    },
};

export default dashboardLayoutEditorTabs;
